/**
 * Unified Document Reader Service
 *
 * Single entry point for all document analysis operations, ensuring consistent
 * behavior regardless of how the analysis is initiated (orchestrator, direct tool,
 * button click, or workflow chaining).
 *
 * Features:
 * - Cache-aware: Checks sharedContext.documentContent before analyzing
 * - History integration: Pushes to contentHistory on fresh analysis
 * - Consistent error handling across all entry points
 * - Transaction recording for cost tracking
 * - Unified logging with source identification
 */

import { analyzeDocumentsWithClaude, hasAnalyzableFiles } from '../claudeDocumentAnalyzer';
import { CLAUDE_DOCUMENT_ANALYSIS_DISPLAY } from '../../config/agenticWorkflowConfig';
import { storeDocumentAnalysis } from './workflowOutputStore';

import type { SharedWorkflowContext, TokenUsageMetadata } from './types';

// ============================================================================
// Model Display Name Helper — single consolidated builder (2026-07-06: the
// local copy here had drifted — raw-vendor-id fallthrough, unversioned Opus
// pinned to Apex-4.7 instead of 4.8, no Nova-5 branch).
// ============================================================================

import { buildModelDisplay } from './modelDisplay';

// ============================================================================
// Types
// ============================================================================

/**
 * Transaction recorder function type
 */
export type AITransactionRecorder = (metadata: {
  tokenUsage: TokenUsageMetadata;
  source: string;
  modelOverride?: string;
  summary?: string;
}) => void;

/**
 * Source identifier for document analysis requests
 */
export type DocumentAnalysisSource = 'pre-orchestrator' | 'orchestrator' | 'direct-tool' | 'button-click' | 'workflow';

/**
 * Parameters for unified document reading
 */
export interface ReadDocumentsParams {
  /** Files to analyze */
  files: File[];
  /** User's query/context for the analysis */
  query?: string;
  /** Abort signal for cancellation */
  signal?: AbortSignal;
  /** Shared context for caching and history */
  sharedContext: SharedWorkflowContext;
  /** Transaction recorder for cost tracking */
  recordTransaction?: AITransactionRecorder;
  /** Whether to force fresh analysis (bypass cache) */
  forceRefresh?: boolean;
  /** Source identifier for logging/debugging */
  source: DocumentAnalysisSource;
  /**
   * Called while a SCANNED document is rendered and transcribed.
   *
   * WHY (2026-08-21): a 30-page scan renders every page and then makes a
   * vision call per batch of four — minutes of work behind a spinner that said
   * only "reading attachment". Working and hung looked identical, to the user
   * and to us.
   */
  onReadProgress?: (info: { phase: string; done: number; total: number }) => void;
}

/**
 * Result from unified document reading
 */
export interface ReadDocumentsResult {
  /** The analysis text */
  analysis: string;
  /** Number of files successfully analyzed */
  filesAnalyzed: number;
  /** Files that were skipped */
  skippedFiles: string[];
  /** Whether result came from cache */
  fromCache: boolean;
  /** RAW untruncated source text, when client-side extraction produced it. */
  fullText?: string;
  /** Token usage (only if fresh analysis) */
  tokenUsage?: TokenUsageMetadata;
}

export interface ReadDocumentsInWavesParams extends ReadDocumentsParams {
  waveTokenBudget?: number;
  perFileTimeoutMs?: number;
  onProgress?: (progress: { processed: number; total: number; fileName: string }) => void;
}

/**
 * Document analysis metadata for shared context
 */
export interface DocumentAnalysisMeta {
  filesAnalyzed: number;
  fileNames: string[];
  analyzedAt: Date;
  source: DocumentAnalysisSource;
  /** Stable hash of the attachment packet that produced this analysis.
   *  Used to detect when a new message has a different set of files,
   *  preventing cache cross-contamination across cases in the same thread. */
  fingerprint: string;
}

// ============================================================================
// Attachment Fingerprinting (FH-CLASSIFIER-005)
// ============================================================================

/**
 * Compute a stable, deterministic fingerprint for a set of File attachments.
 * Uses file count, sorted file names, sizes, MIME types, and lastModified
 * timestamps to produce a diff-able hash. Two different packets will almost
 * always produce different fingerprints; the same packet re-attached will match.
 *
 * Uses DJB2 hash (fast, deterministic, no crypto dependency).
 */
export function computeAttachmentFingerprint(files: File[]): string {
  if (!files || files.length === 0) return 'empty';

  const parts = files
    .map((f) => `${f.name}|${f.size}|${f.type || 'unknown'}|${f.lastModified}`)
    .sort();
  const joined = `${files.length}:${parts.join('::')}`;

  // DJB2 hash → hex string
  let hash = 5381;
  for (let i = 0; i < joined.length; i++) {
    hash = ((hash << 5) + hash + joined.charCodeAt(i)) | 0;
  }
  return 'fp:' + (hash >>> 0).toString(16).padStart(8, '0');
}

// ============================================================================
// Core Function
// ============================================================================

/**
 * Unified document reader - single entry point for all document analysis.
 *
 * This function ensures consistent behavior across all entry points:
 * - Orchestrator path (message with attachment)
 * - Direct tool invocation
 * - Button click workflows
 * - Workflow chaining
 *
 * @param params - Read documents parameters
 * @returns Document analysis result with cache status
 */
export async function readDocuments(
  params: ReadDocumentsParams
): Promise<ReadDocumentsResult> {
  const {
    files,
    query,
    signal,
    sharedContext,
    recordTransaction,
    forceRefresh = false,
    source,
    onReadProgress,
  } = params;

  console.log(`[UnifiedDocReader] Entry from: ${source}`);
  console.log(`[UnifiedDocReader] Files: ${files?.length || 0}`);

  // 1. Early exit if no files
  if (!files || files.length === 0) {
    console.log('[UnifiedDocReader] No files to analyze');
    return {
      analysis: '',
      filesAnalyzed: 0,
      skippedFiles: [],
      fromCache: false,
    };
  }

  // 2. Check analyzability
  if (!hasAnalyzableFiles(files)) {
    const skipped = files.map((f) => f.name);
    console.log(`[UnifiedDocReader] No analyzable files: ${skipped.join(', ')}`);
    return {
      analysis: '',
      filesAnalyzed: 0,
      skippedFiles: skipped,
      fromCache: false,
    };
  }

  // 3. Check cache (unless force refresh, and validate fingerprint)
  if (!forceRefresh && sharedContext.documentContent) {
    const currentFingerprint = computeAttachmentFingerprint(files);
    const cachedFingerprint = sharedContext.documentAnalysisMeta?.fingerprint ?? '';
    if (currentFingerprint && cachedFingerprint && currentFingerprint === cachedFingerprint) {
      console.log('[UnifiedDocReader] Using cached analysis (fingerprint match: ' + currentFingerprint + ')');
      return {
        analysis: sharedContext.documentContent,
        filesAnalyzed: files.length,
        skippedFiles: [],
        fromCache: true,
        ...(sharedContext.documentFullTextRef?.current || sharedContext.documentFullText
          ? { fullText: (sharedContext.documentFullTextRef?.current || sharedContext.documentFullText) as string }
          : {}),
      };
    }
    console.log('[UnifiedDocReader] Cache miss — fingerprint mismatch (current=' +
      currentFingerprint + ' cached=' + cachedFingerprint + '); forcing fresh analysis');
    // Clear stale cache so downstream code doesn't see the wrong content
    invalidateDocumentCache(sharedContext);
  }

  // 4. Perform fresh analysis
  console.log('[UnifiedDocReader] Performing fresh analysis...');

  try {
    const result = await analyzeDocumentsWithClaude({
      files,
      userQuery: query,
      signal,
      onProgress: onReadProgress,
    });

    // 5. Cache result in sharedContext - SET BOTH STATE AND REF
    sharedContext.setDocumentContent(result.analysis);
    // Also set ref synchronously to avoid React state timing issues
    // (React state updates are async, but downstream workflows check immediately)
    if (sharedContext.documentContentRef) {
      sharedContext.documentContentRef.current = result.analysis;
    }
    // Cache the RAW untruncated text too (2026-08-11). Whole-document tools
    // read this rather than the analyzer's capped summary — translating the
    // summary is what shipped a 10-page rendering of a 200-page dossier.
    const rawFullText = result.extractedFullText || null;
    sharedContext.setDocumentFullText?.(rawFullText);
    if (sharedContext.documentFullTextRef) {
      sharedContext.documentFullTextRef.current = rawFullText;
    }
    console.log(
      `[UnifiedDocReader] Cached in sharedContext (state + ref)` +
      `${rawFullText ? ` + ${rawFullText.length} chars of raw source text (${result.fullTextSource || 'text-layer'})` : ' (no raw text — vision path)'}`,
    );

    // 6. Store in WorkflowOutputStore for button click bridging
    storeDocumentAnalysis({
      analysis: result.analysis,
      filesAnalyzed: result.filesAnalyzed,
      fileNames: files.map((f) => f.name),
      timestamp: new Date(),
      source,
    });
    console.log('[UnifiedDocReader] Stored in WorkflowOutputStore for button bridging');

    // 7. Store metadata if setter is available (includes fingerprint)
    if (sharedContext.setDocumentAnalysisMeta) {
      sharedContext.setDocumentAnalysisMeta({
        filesAnalyzed: result.filesAnalyzed,
        fileNames: files.map((f) => f.name),
        analyzedAt: new Date(),
        source,
        fingerprint: computeAttachmentFingerprint(files),
        ...(result.fullTextSource ? { fullTextSource: result.fullTextSource } : {}),
      });
      console.log('[UnifiedDocReader] Stored analysis metadata');
    }

    // 8. Push to content history (for workflow chaining)
    if (result.analysis && sharedContext.pushContentHistory) {
      sharedContext.pushContentHistory({
        content: result.analysis,
        title: 'Document Transcription',
        timestamp: new Date(),
        source: 'research', // Treat document transcription as research content for chaining
      });
      console.log('[UnifiedDocReader] Pushed to content history');
    }

    // 9. Create context-only history item for Firestore persistence
    // This ensures full document analysis is preserved in conversation history
    // but hidden from UI (isContextOnly: true)
    if (result.analysis && sharedContext.appendContextMessage) {
      sharedContext.appendContextMessage({
        text: result.analysis,
        isUser: true, // User attached the document
        timestamp: new Date(),
        isContextOnly: true, // Hidden from UI, included in AI context
        sourceTag: 'document_analysis',
        attachmentsSerialized: files.map((f) => ({
          name: f.name,
          type: f.type,
          size: f.size,
        })),
      });
      console.log('[UnifiedDocReader] Created context-only history item for Firestore persistence');
    }

    // 10. Record transaction if recorder provided
    if (result.tokenUsage && recordTransaction) {
      recordTransaction({
        tokenUsage: result.tokenUsage,
        source: 'agenticWorkflows',
        modelOverride: buildModelDisplay(result.tokenUsage, 'Document Analysis', CLAUDE_DOCUMENT_ANALYSIS_DISPLAY),
        summary: 'read document',
      });
      console.log('[UnifiedDocReader] Transaction recorded');
    }

    return {
      analysis: result.analysis,
      filesAnalyzed: result.filesAnalyzed,
      skippedFiles: result.skippedFiles,
      fromCache: false,
      tokenUsage: result.tokenUsage,
      ...(result.extractedFullText ? { fullText: result.extractedFullText } : {}),
    };
  } catch (error) {
    console.error(`[UnifiedDocReader] Analysis failed:`, error);
    throw error;
  }
}

function estimateAttachmentTokens(file: File): number {
  const mime = (file.type || '').toLowerCase();
  if (mime.startsWith('text/') || mime.includes('json') || mime.includes('xml')) {
    return Math.max(60, Math.ceil(file.size / 4));
  }
  if (mime.includes('pdf')) return Math.max(140, Math.ceil(file.size / 2500));
  if (mime.startsWith('image/')) return Math.max(180, Math.ceil(file.size / 1800));
  return Math.max(100, Math.ceil(file.size / 2200));
}

function packFilesIntoTokenWaves(files: File[], tokenBudget: number): File[][] {
  const waves: File[][] = [];
  let current: File[] = [];
  let currentTokens = 0;
  for (const file of files) {
    const tokens = estimateAttachmentTokens(file);
    if (tokens >= tokenBudget) {
      if (current.length > 0) {
        waves.push(current);
        current = [];
        currentTokens = 0;
      }
      waves.push([file]);
      continue;
    }
    if (current.length > 0 && currentTokens + tokens > tokenBudget) {
      waves.push(current);
      current = [file];
      currentTokens = tokens;
      continue;
    }
    current.push(file);
    currentTokens += tokens;
  }
  if (current.length > 0) waves.push(current);
  return waves;
}

export async function readDocumentsInWaves(
  params: ReadDocumentsInWavesParams,
): Promise<ReadDocumentsResult> {
  const {
    files,
    query,
    signal,
    sharedContext,
    recordTransaction,
    source,
    waveTokenBudget = 2400,
    perFileTimeoutMs = 45_000,
    onProgress,
  } = params;

  if (!Array.isArray(files) || files.length === 0) {
    return { analysis: '', filesAnalyzed: 0, skippedFiles: [], fromCache: false };
  }

  const waves = packFilesIntoTokenWaves(files, waveTokenBudget);
  const analysisParts: string[] = [];
  const skippedFiles: string[] = [];
  let filesAnalyzed = 0;
  let processed = 0;
  let tokenUsage: TokenUsageMetadata | undefined;

  for (const wave of waves) {
    for (const file of wave) {
      if (signal?.aborted) {
        throw new DOMException('Aborted', 'AbortError');
      }
      processed += 1;
      onProgress?.({ processed, total: files.length, fileName: file.name });
      let timeoutId: ReturnType<typeof setTimeout> | undefined;
      try {
        const readPromise = readDocuments({
          files: [file],
          query,
          signal,
          sharedContext,
          recordTransaction,
          forceRefresh: true,
          source,
        });
        const timeoutPromise = new Promise<never>((_, reject) => {
          timeoutId = setTimeout(() => {
            reject(new Error(`per-file timeout after ${Math.round(perFileTimeoutMs / 1000)}s`));
          }, perFileTimeoutMs);
        });
        const result = await Promise.race([readPromise, timeoutPromise]);
        if (result.analysis) {
          analysisParts.push(`[doc ${processed}: ${file.name}]\n${result.analysis}`);
          filesAnalyzed += result.filesAnalyzed;
          if (!tokenUsage && result.tokenUsage) tokenUsage = result.tokenUsage;
        } else {
          skippedFiles.push(file.name);
          analysisParts.push(`[couldn't fully read doc ${processed}]`);
        }
      } catch (err) {
        console.warn('[UnifiedDocReader] per-file graceful degradation triggered', {
          file: file.name,
          error: err instanceof Error ? err.message : String(err),
        });
        skippedFiles.push(file.name);
        analysisParts.push(`[couldn't fully read doc ${processed}]`);
      } finally {
        if (timeoutId !== undefined) clearTimeout(timeoutId);
      }
    }
  }

  const merged = analysisParts.join('\n\n');
  sharedContext.setDocumentContent(merged || null);
  if (sharedContext.documentContentRef) {
    sharedContext.documentContentRef.current = merged;
  }

  return {
    analysis: merged,
    filesAnalyzed,
    skippedFiles,
    fromCache: false,
    tokenUsage,
  };
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Invalidate document cache in shared context.
 * Call this when user attaches new files or explicitly requests re-analysis.
 *
 * @param sharedContext - The shared workflow context
 */
export function invalidateDocumentCache(
  sharedContext: SharedWorkflowContext
): void {
  console.log('[UnifiedDocReader] Invalidating cache');
  sharedContext.setDocumentContent(null);
  // Clear the raw text in lockstep — a stale full text outliving its summary
  // would let the next document be translated from the previous one.
  sharedContext.setDocumentFullText?.(null);
  if (sharedContext.documentFullTextRef) {
    sharedContext.documentFullTextRef.current = null;
  }
  if (sharedContext.setDocumentAnalysisMeta) {
    sharedContext.setDocumentAnalysisMeta(undefined);
  }
}

/**
 * Check if document cache is populated.
 *
 * @param sharedContext - The shared workflow context
 * @returns True if cache contains document content
 */
export function hasDocumentCache(
  sharedContext: SharedWorkflowContext
): boolean {
  return !!sharedContext.documentContent;
}

/**
 * Get document analysis metadata from shared context.
 *
 * @param sharedContext - The shared workflow context
 * @returns Document analysis metadata, or null if not available
 */
export function getDocumentAnalysisMeta(
  sharedContext: SharedWorkflowContext
): DocumentAnalysisMeta | null {
  return sharedContext.documentAnalysisMeta || null;
}

// Re-export hasAnalyzableFiles for convenience
export { hasAnalyzableFiles };
